import React from 'react';
import 'flag-icons/css/flag-icons.min.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Avatar component with DiceBear fallback
 * @param {Object} props
 * @param {string} props.userId - User ID for custom avatar
 * @param {string} props.avatarUrl - Custom avatar URL from user profile
 * @param {string} props.fullImageUrl - Full image URL (optional)
 * @param {string} props.userName - User name for DiceBear seed
 * @param {string} props.userEmail - User email as fallback seed
 * @param {number} props.size - Avatar size in pixels (default: 40)
 * @param {string} props.className - Additional CSS classes
 * @param {function} props.onFullImageClick - Callback when avatar is clicked and full image exists
 * @param {boolean} props.clickable - Whether avatar is clickable (default: true if fullImageUrl exists)
 * @param {string} props.country - ISO 3166-1 alpha-2 country code, e.g. "RU". When set, renders a flag badge at bottom-right.
 */
const Avatar = ({ 
  userId, 
  avatarUrl,
  fullImageUrl,
  userName, 
  userEmail, 
  size = 40,
  className = '',
  onFullImageClick,
  onClick,
  clickable,
  country,
}) => {
  const [imgError, setImgError] = React.useState(false);
  const isClickable = clickable !== undefined ? clickable : !!fullImageUrl;
  
  // Generate DiceBear URL as fallback
  const getDiceBearUrl = () => {
    const seed = userName || userEmail || userId || 'default';
    // Using 'avataaars' style for cartoon avatars (like Slack)
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(seed)}&backgroundColor=667eea`;
  };

  // Determine which avatar to show
  const getAvatarSrc = () => {
    // If custom avatar exists and hasn't errored
    if (avatarUrl && !imgError) {
      // Add cache-busting parameter to prevent stale cached images
      const cacheBust = new URLSearchParams(window.location.search).get('t') || '';
      const separator = avatarUrl.includes('?') ? '&' : '?';
      const cacheBustSuffix = cacheBust ? `${separator}t=${cacheBust}` : '';
      
      // If it's already a full URL (starts with http), use as is
      if (avatarUrl.startsWith('http')) {
        return avatarUrl + cacheBustSuffix;
      }
      // Otherwise, prepend backend URL
      return `${BACKEND_URL}${avatarUrl}${cacheBustSuffix}`;
    }
    // Fallback to DiceBear
    return getDiceBearUrl();
  };

  const handleError = () => {
    setImgError(true);
  };

  const handleClick = (e) => {
    if (!isClickable) return;
    e.stopPropagation();
    
    // If onClick prop provided, use it (e.g., for navigation)
    if (onClick) {
      onClick(e);
      return;
    }
    
    // Otherwise, handle full image click
    if (fullImageUrl && onFullImageClick) {
      const fullUrl = fullImageUrl.startsWith('http') 
        ? fullImageUrl 
        : `${BACKEND_URL}${fullImageUrl}`;
      onFullImageClick(fullUrl);
    }
  };

  return (
    <span
      className={country ? 'avatar-with-flag' : undefined}
      style={{
        position: 'relative',
        display: 'inline-flex',
        verticalAlign: 'middle',
        width: size,
        height: size,
        flexShrink: 0,
        lineHeight: 0,
      }}
      data-country={country || undefined}
    >
      <img
        src={getAvatarSrc()}
        alt={userName || 'User avatar'}
        className={className}
        onError={handleError}
        onClick={handleClick}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          display: 'block',
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'transform 0.2s',
        }}
        onMouseEnter={(e) => {
          if (isClickable) e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          if (isClickable) e.currentTarget.style.transform = 'scale(1)';
        }}
      />
      {country && (
        <span
          className={`fi fi-${String(country).toLowerCase()}`}
          title={country}
          aria-label={`Country: ${country}`}
          data-testid={`avatar-flag-${String(country).toLowerCase()}`}
          style={{
            position: 'absolute',
            right: '-6%',
            bottom: '-8%',
            width: Math.max(18, Math.round(size * 0.45)),
            height: Math.max(14, Math.round(size * 0.34)),
            borderRadius: Math.max(2, Math.round(size * 0.04)),
            border: 'none',
            boxShadow: '0 1px 3px rgba(0,0,0,0.35)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'block',
            pointerEvents: 'none',
          }}
        />
      )}
    </span>
  );
};

export default Avatar;
